import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/lib/supabase";
import { MARKETING_URL } from "@/lib/urls";
import {
  fetchSharedMemoryByToken,
  type SharedMemoryData,
  type SharedMemoryParticipant,
} from "@/hooks/queries/useSharedMemories";

type ParticipantRow = SharedMemoryParticipant;

type PageState = "loading" | "valid" | "expired" | "not_found";

const categoryStyles: Record<string, string> = {
  Milestone:    "bg-category-milestone text-category-milestone-text border-transparent",
  Funny:        "bg-category-funny text-category-funny-text border-transparent",
  Drawing:      "bg-category-drawing text-category-drawing-text border-transparent",
  Conversation: "bg-category-conversation text-category-conversation-text border-transparent",
  First:        "bg-category-first text-category-first-text border-transparent",
  Thankful:     "bg-category-thankful text-category-thankful-text border-transparent",
  Travel:       "bg-category-travel text-category-travel-text border-transparent",
  Achievement:  "bg-category-achievement text-category-achievement-text border-transparent",
  Contribution: "bg-category-contribution text-category-contribution-text border-transparent",
};

const fallbackStyle = "bg-muted text-muted-foreground border-transparent";

function formatMemoryDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

function extractAudioPath(audioUrl: string): string | null {
  if (!audioUrl) return null;
  if (!audioUrl.startsWith("http")) return audioUrl.split("?")[0];
  const marker = "/audio-memories/";
  const idx = audioUrl.indexOf(marker);
  if (idx !== -1) return audioUrl.slice(idx + marker.length).split("?")[0];
  return null;
}

const SharedMemory = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const [pageState, setPageState] = useState<PageState>("loading");
  const [memory, setMemory] = useState<SharedMemoryData | null>(null);
  const [shareExpiresAt, setShareExpiresAt] = useState<string | null>(null);
  const [signedAudioUrl, setSignedAudioUrl] = useState<string | null>(null);
  const [signedImageUrl, setSignedImageUrl] = useState<string | null>(null);

  // Inject noindex meta tags — this page should not be indexed by search engines.
  useEffect(() => {
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);

    const googlebot = document.createElement("meta");
    googlebot.name = "googlebot";
    googlebot.content = "noindex";
    document.head.appendChild(googlebot);

    return () => {
      document.head.removeChild(robots);
      document.head.removeChild(googlebot);
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setPageState("not_found");
      return;
    }

    fetchSharedMemoryByToken(token).then(async (result) => {
        if (!result) {
          // Token not found through RLS (expired, revoked, or never existed).
          // Call the SECURITY DEFINER function to distinguish the two cases.
          const { data: status } = await supabase.rpc("check_share_token", {
            p_token: token,
          });
          const s = status as { found: boolean; expired: boolean } | null;
          if (s?.found) {
            setPageState("expired");
          } else {
            setPageState("not_found");
          }
          return;
        }

        const { memory: mem, expiresAt } = result;

        setMemory(mem);
        setShareExpiresAt(expiresAt);
        setPageState("valid");

        // Track view — fire and forget, never block render.
        void supabase.rpc("increment_share_view", { p_token: token });

        // Sign audio URL (1-hour expiry, private bucket)
        if (mem.audio_url) {
          const path = extractAudioPath(mem.audio_url);
          if (path) {
            supabase.storage
              .from("audio-memories")
              .createSignedUrl(path, 3600)
              .then(({ data: signed }) => {
                if (signed?.signedUrl) setSignedAudioUrl(signed.signedUrl);
              });
          }
        }

        // Sign image URL (1-hour expiry, private bucket)
        if (mem.image_url) {
          supabase.storage
            .from("memory-images")
            .createSignedUrl(mem.image_url, 3600)
            .then(({ data: signed }) => {
              if (signed?.signedUrl) setSignedImageUrl(signed.signedUrl);
            });
        }
      });
  }, [token]);

  // ── Derived values ────────────────────────────────────────────────────────

  const participantNames = memory?.memory_participants
    .map((p) => {
      if (p.child) return p.child.name;
      if (p.member) return p.member.display_name;
      if (p.display_name) return p.display_name;
      return null;
    })
    .filter(Boolean) as string[] | undefined;

  const daysLeft = shareExpiresAt
    ? Math.ceil(
        (new Date(shareExpiresAt).getTime() - Date.now()) /
          (1000 * 60 * 60 * 24),
      )
    : null;

  const expiryLabel = (() => {
    if (daysLeft === null) return null;
    if (daysLeft <= 0) return t("share.page.expiresToday");
    if (daysLeft === 1) return t("share.page.expiresIn_one", { count: 1 });
    return t("share.page.expiresIn_other", { count: daysLeft });
  })();

  // ── States ────────────────────────────────────────────────────────────────

  if (pageState === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <span className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (pageState === "expired") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="max-w-sm w-full text-center space-y-5">
          <div className="text-4xl">🌰</div>
          <h1 className="font-display text-2xl tracking-tight text-foreground">
            {t("share.page.expired.heading")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("share.page.expired.subtext")}
          </p>
          <a
            href={`${MARKETING_URL}?ref=share`}
            className="block w-full rounded-2xl px-5 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: "#D85A30" }}
          >
            {t("share.page.expired.cta")}
          </a>
          <Link to="/" className="block text-xs text-muted-foreground hover:text-foreground">
            Back to Mori
          </Link>
        </div>
      </div>
    );
  }

  if (pageState === "not_found" || !memory) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="max-w-sm text-center space-y-3">
          <h1 className="font-display text-2xl tracking-tight text-foreground">
            {t("share.page.notFound.heading")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("share.page.notFound.subtext")}
          </p>
        </div>
      </div>
    );
  }

  // ── Valid memory view ─────────────────────────────────────────────────────

  const hasNames = participantNames && participantNames.length > 0;

  return (
    <div className="min-h-screen bg-background animate-page-enter">
      <div className="mx-auto w-full max-w-[430px] px-5 py-12 space-y-6">

        {/* Branding header */}
        <div className="text-center">
          <span className="font-display text-lg tracking-tight text-foreground">
            Mori 🌰
          </span>
          {hasNames && (
            <p className="text-sm text-muted-foreground mt-1">
              {t("share.page.from", { names: participantNames!.join(" & ") })}
            </p>
          )}
        </div>

        {/* Read-only memory card */}
        <div className="rounded-2xl bg-card p-5 shadow-sm border border-border/50 space-y-3">
          {/* Row 1: date + participant names */}
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
              {formatMemoryDate(memory.created_at)}
            </p>
            {hasNames && (
              <span className="text-xs text-muted-foreground">
                {participantNames!.join(", ")}
              </span>
            )}
          </div>

          {/* Category badges */}
          <div className="flex flex-wrap gap-1">
            {memory.categories?.map((cat) => (
              <Badge
                key={cat}
                className={categoryStyles[cat] ?? fallbackStyle}
                variant="secondary"
              >
                {cat}
              </Badge>
            ))}
          </div>

          {/* Image — signed URL for private bucket */}
          {signedImageUrl && (
            <img
              src={signedImageUrl}
              alt={memory.title ?? "Memory"}
              className="w-full rounded-xl object-cover max-h-80"
            />
          )}

          {/* Chapter text — only AI-generated content, never raw transcript */}
          {memory.ai_chapter && (
            <div className="space-y-2">
              <p
                className="text-sm leading-relaxed text-foreground"
                style={{ fontFamily: "Georgia, serif", lineHeight: 1.7 }}
              >
                {memory.ai_chapter}
              </p>
              <p
                className="text-muted-foreground/70"
                style={{ fontSize: 10 }}
              >
                🌰 {t("mori.keptBy")}
              </p>
            </div>
          )}

          {/* Audio player — uses short-lived signed URL */}
          {signedAudioUrl && (
            <audio
              controls
              src={signedAudioUrl}
              style={{ display: "block", width: "100%", height: 36 }}
            />
          )}
        </div>

        {/* Expiry notice */}
        {expiryLabel && (
          <p className="text-center text-xs text-muted-foreground">
            {expiryLabel}
          </p>
        )}

        {/* Mori CTA banner */}
        <div
          className="rounded-2xl p-5 space-y-3"
          style={{ backgroundColor: "#FDF0EE" }}
        >
          <p className="text-sm font-semibold" style={{ color: "#8B3A2E" }}>
            {t("share.cta.heading")}
          </p>
          <p className="text-xs" style={{ color: "#8B3A2E", opacity: 0.7 }}>
            {t("share.cta.subtext")}
          </p>
          <a
            href={`${MARKETING_URL}?ref=share`}
            className="block w-full rounded-xl py-2.5 text-center text-sm font-semibold text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: "#D85A30" }}
          >
            {t("share.cta.button")}
          </a>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          Memories kept safe by{" "}
          <a
            href={MARKETING_URL}
            className="text-primary hover:underline font-medium"
          >
            meetmori.app
          </a>
        </p>
      </div>
    </div>
  );
};

export default SharedMemory;
